import { LEVELS, isUnlocked } from '../levels/index.js';
import { hasRedline, redlineKey } from '../gameplay/Redline.js';
import { PAD_ACTIONS, loadBinds, bindLabel } from '../input/GamepadMap.js';
import { KEY_ACTIONS, loadKeyBinds, keyLabel } from '../input/KeyMap.js';
import { CHANGELOG } from '../core/Changelog.js';
import { formatSyncCode } from '../core/Sync.js';
import { isSlurHandle } from '../core/NamePolicy.js';
import pkg from '../../package.json';
import { gameModes } from '../gameplay/GameModes.js';
import { formatTime } from './HUD.js';
import { supported as fullscreenSupported } from './Fullscreen.js';
import { isTouchDevice } from '../render/Quality.js';

const $ = (id) => document.getElementById(id);

/**
 * Menu layer. Owns which screen is visible and turns clicks into intents;
 * it never touches the simulation directly, so the game can be driven from
 * anywhere else (a cheat console, a demo reel) without going through here.
 */
export class Screens {
  constructor({ save, settings, audio, on }) {
    this.save = save;
    this.settings = settings;
    this.audio = audio;
    this.on = on;                 // (intent, payload) => void
    this.root = $('screens');
    this.stack = [];
    this.current = null;
    this._bind();
  }

  _click(el, fn, sound = 'click') {
    if (!el) return;
    el.addEventListener('click', (e) => {
      e.preventDefault();
      this.audio?.unlock();
      this.audio?.ui(sound);
      fn(e);
    });
  }

  _bind() {
    this._click($('go-play'), () => this.on('play'));
    this._click($('pad-defaults'), () => {
      this.settings.set('padBinds', '');
      this._renderPadBinds();
    });
    this._renderPadBinds();
    this._click($('go-controls'), () => { this._renderKeyBinds(); this.show('controls'); });
    this._click($('go-sync'), () => {
      this._renderSync();
      this.show('sync');
    });
    this._click($('sync-push'), () => this.on('sync-push'));
    this._click($('sync-pull'), () => this.on('sync-pull', $('sync-input')?.value ?? ''));
    this._click($('key-defaults'), () => {
      this.settings.set('keyBinds', '{}');
      this._renderKeyBinds();
    });
    this._renderKeyBinds();

    const vt = $('version-tag');
    if (vt) {
      vt.textContent = 'V' + pkg.version;
      this._click(vt, () => { this._renderChangelog(); this.show('changelog'); });
    }

    // Escape walks menus backwards: pause resumes, everything else takes
    // the screen's own BACK path (so leaving a race lobby still leaves the
    // room). Terminal screens (results, dead, the callsign gate) keep
    // their explicit buttons; backing out of them by reflex loses runs.
    document.addEventListener('keydown', (e) => {
      if (e.code !== 'Escape' || !this.current) return;
      if (this.current === 'pause') { this.on('resume'); return; }
      if (['results', 'dead', 'callsign', 'title'].includes(this.current)) return;
      document.querySelector('.screen.on [data-back]')?.click();
    });
    this._click($('go-daily'), () => this.on('daily'));
    this._click($('go-gauntlet'), () => this.on('gauntlet'));
    this._click($('go-marathon'), () => this.on('marathon'));
    this._click($('go-race'), () => {
      this._fillRaceLevels();
      const rn = $('race-name');
      if (rn) rn.value = this.settings.get('handle') ?? '';
      this.show('race');
    });
    $('race-name')?.addEventListener('change', () => {
      // One identity, two doors: this field and settings write the same
      // handle, so nobody needs to know the settings screen exists to
      // have a name in a room.
      const raw = $('race-name').value.trim().toUpperCase().slice(0, 14);
      // The policy snaps a rejected name back to the standing one; the
      // Worker enforces the same list, this is just the polite version.
      const v = isSlurHandle(raw) ? (this.settings.get('handle') ?? '') : raw;
      $('race-name').value = v;
      this.settings.set('handle', v);
      this.on('race-name', v);
    });
    this._click($('race-create'), () => this.on('race-create'));
    this._click($('race-join'), () => this.on('race-join', $('race-code')?.value ?? ''));
    this._click($('race-start'), () => this.on('race-start'));
    $('race-level')?.addEventListener('change', () => {
      this.audio?.ui('click');
      this.on('race-vote', $('race-level')?.value ?? '');
    });
    this._click($('race-leave'), () => this.on('race-leave'), 'back');
    for (const b of document.querySelectorAll('[data-ping]')) {
      this._click(b, () => this.on('race-ping', b.dataset.ping));
    }
    this._click($('go-levels'), () => this.show('levels'));
    this._click($('go-howto'), () => this.show('howto'));
    this._click($('go-settings'), () => this.show('settings'));

    for (const el of document.querySelectorAll('[data-back]')) {
      this._click(el, () => this.back(), 'back');
    }

    this._click($('pz-resume'), () => this.on('resume'));
    this._click($('pz-restart'), () => this.on('restart'));
    this._click($('pz-settings'), () => this.show('settings'));
    this._click($('pz-quit'), () => this.on('quit'), 'back');
    this._click($('pz-room'), () => this.on('race-lobby'));

    // Feedback: reachable from pause, results and death (the moments a
    // player actually has something to say). Screens own the form UX;
    // the Game attaches context and POSTs, then reports back through
    // feedbackDone.
    for (const b of document.querySelectorAll('.go-feedback')) {
      this._click(b, () => this.show('feedback'));
    }
    this._fbCat = 'idea';
    const cats = document.querySelectorAll('#fb-cats [data-cat]');
    cats.forEach((b) => {
      b.classList.toggle('on', b.dataset.cat === this._fbCat);
      this._click(b, () => {
        this._fbCat = b.dataset.cat;
        cats.forEach((x) => x.classList.toggle('on', x === b));
      });
    });
    this._click($('fb-send'), () => {
      const text = $('fb-text').value.trim();
      if (!text) { $('fb-text').focus(); return; }
      $('fb-send').disabled = true;
      $('fb-send').textContent = 'TRANSMITTING...';
      this.on('feedback', { category: this._fbCat, text });
    });

    this._click($('rs-next'), () => this.on('next'));
    this._click($('rs-share'), () => this.on('share'));
    this._click($('ch-go'), () => this.on('challenge-go'));
    this._click($('ch-menu'), () => this.on('quit'), 'back');
    this._click($('rs-room'), () => this.on('race-lobby'));
    this._click($('rs-rematch'), () => this.on('race-rematch'));
    this._click($('rs-spectate'), () => this.on('race-spectate'));
    this._click($('rs-retry'), () => this.on('restart'));
    this._click($('rs-menu'), () => this.on('levels'), 'back');

    this._click($('dz-respawn'), () => this.on('respawn'));
    this._click($('dz-restart'), () => this.on('restart'));
    this._click($('dz-quit'), () => this.on('quit'), 'back');

    this._click($('wipe-save'), () => {
      this.save.wipe();
      // The callsign is progress, not a preference: leaving it behind made
      // a wiped device skip the first-run naming prompt (reported).
      this.settings.set('handle', '');
      this.buildLevelGrid();
      this.on('toast', 'PROGRESS WIPED');
    }, 'back');

    this._click($('btn-pause'), () => this.on('pause'));

    this._click($('callsign-go'), () => {
      let v = ($('first-handle')?.value ?? '').trim().toUpperCase().slice(0, 14)
        || this._callsignDefault || 'PILOT';
      if (isSlurHandle(v)) v = this._callsignDefault || 'PILOT';
      this.settings.set('handle', v);
      const go = this._callsignGo;
      this._callsignGo = null;
      go?.();
    });

    this._bindSettings();
    this.buildLevelGrid();
  }

  // ---------------------------------------------------------------- screens

  show(name, { replace = false } = {}) {
    if (this.current && !replace) this.stack.push(this.current);
    // A history entry per menu depth, so the Android back gesture unwinds
    // menus instead of quitting the app (TWAs have no other navigation).
    if (name !== 'title') {
      try { history.pushState({ m: name }, ''); } catch { /* not available */ }
    }
    this._activate(name);
  }

  replace(name) {
    this.stack.length = 0;
    this._activate(name);
  }

  back() {
    const prev = this.stack.pop();
    if (prev) this._activate(prev);
    else this._activate('title');
  }

  /** The Game reports the POST outcome; the form narrates it and returns. */
  feedbackDone(ok, reason = null) {
    const b = $('fb-send');
    if (!b) return;
    // A server refusal is not a network problem: retrying the same text
    // will never help, so say what actually happened.
    b.textContent = ok ? 'TRANSMITTED. THANK YOU.'
      : reason ? 'NOT SENT: TOO LONG OR EMPTY'
        : 'NO SIGNAL: TRY AGAIN LATER';
    if (ok) $('fb-text').value = '';
    setTimeout(() => {
      b.disabled = false;
      b.textContent = 'SEND';
      if (ok && this.current === 'feedback') this.back();
    }, 1400);
  }

  hide() {
    this.root?.classList.remove('on');
    for (const s of document.querySelectorAll('.screen')) s.classList.remove('on');
    this.current = null;
    this.stack.length = 0;
  }

  _activate(name) {
    this.current = name;
    this.root?.classList.add('on');
    for (const s of document.querySelectorAll('.screen')) s.classList.remove('on');
    $(`screen-${name}`)?.classList.add('on');
    if (name === 'levels') this.buildLevelGrid();
    if (name === 'settings') this.syncSettings();
  }

  get visible() { return !!this.current; }

  // ------------------------------------------------------------ level grid

  buildLevelGrid() {
    const grid = $('level-grid');
    if (!grid) return;
    grid.innerHTML = '';
    LEVELS.forEach((lv, i) => {
      const unlocked = isUnlocked(this.save, lv.id);
      const best = this.save.bestTime(lv.id);
      const card = document.createElement('button');
      card.className = `lvcard${unlocked ? '' : ' locked'}`;
      const modeLabel = gameModes.meta(lv.mode?.type ?? 'sprint')?.label ?? 'SPRINT';
      // REDLINE (docs/REDLINE.md): offered once the sector is cleared, with
      // its own best. A span, not a button: the card is already one.
      const redline = unlocked && this.save.isCleared(lv.id) && hasRedline(lv);
      const rBest = redline ? this.save.bestTime(redlineKey(lv.id)) : null;
      card.innerHTML = `
        <div class="tagmode">${modeLabel}</div>
        <div class="n">SECTOR ${String(i + 1).padStart(2, '0')}</div>
        <div class="t">${unlocked ? lv.name : 'LOCKED'}</div>
        <div class="d">${unlocked ? lv.blurb : 'CLEAR THE PREVIOUS SECTOR'}</div>
        <div class="meta">
          <span>BEST ${best != null ? formatTime(best) : '--:--.--'}</span>
          <span>PAR ${lv.mode?.par ? formatTime(lv.mode.par) : '--'}</span>
        </div>
        ${redline ? `<span class="rl" role="button" data-id="${lv.id}">REDLINE ${formatTime(lv.redline.seconds)}${rBest != null ? ` // BEST ${formatTime(rBest)}` : ''}</span>` : ''}`;
      if (unlocked) {
        card.addEventListener('click', () => {
          this.audio?.unlock();
          this.audio?.ui('click');
          this.on('play', lv.id);
        });
        card.querySelector('.rl')?.addEventListener('click', (e) => {
          e.stopPropagation();
          this.audio?.unlock();
          this.audio?.ui('click');
          this.on('redline', lv.id);
        });
      }
      grid.appendChild(card);
    });
  }

  // -------------------------------------------------------------- settings

  _bindSettings() {
    const seg = (id, key, cast = String) => {
      const wrap = $(id);
      if (!wrap) return;
      wrap.querySelectorAll('button').forEach((b) => {
        b.addEventListener('click', () => {
          this.audio?.unlock();
          this.audio?.ui('click');
          this.settings.set(key, cast(b.dataset.v));
          this.syncSettings();
        });
      });
    };
    seg('set-quality', 'quality');
    seg('set-invert', 'invertY', Number);
    seg('set-fps', 'showFps', Number);
    seg('set-fullscreen', 'fullscreen', Number);
    seg('set-rival', 'rival', Number);
    seg('set-ghost', 'ghost', Number);
    seg('set-marker', 'marker', Number);

    const handle = $('set-handle');
    handle?.addEventListener('change', () => {
      const raw = handle.value.trim().toUpperCase().slice(0, 14);
      if (isSlurHandle(raw)) {
        handle.value = this.settings.get('handle') ?? '';
        return;
      }
      this.settings.set('handle', raw);
      this.syncSettings();
    });

    // The toggle governs auto-fullscreen on play, which is a touch-only
    // behavior; on desktop it would be a switch wired to nothing, and on
    // iPhone Safari the Fullscreen API does not exist at all.
    if (!fullscreenSupported() || !isTouchDevice()) {
      $('setrow-fullscreen')?.classList.add('hidden');
    }

    const slider = (id, key) => {
      const el = $(id);
      if (!el) return;
      el.addEventListener('input', () => this.settings.set(key, Number(el.value)));
      el.addEventListener('change', () => { this.audio?.unlock(); this.audio?.ui('hover'); });
    };
    slider('set-sens', 'sensitivity');
    slider('set-music', 'music');
    slider('set-sfx', 'sfx');
    slider('set-fov', 'fov');
  }

  syncSettings() {
    const v = this.settings.values;
    const seg = (id, value) => {
      const wrap = $(id);
      if (!wrap) return;
      wrap.querySelectorAll('button').forEach((b) => {
        b.classList.toggle('on', String(b.dataset.v) === String(value));
      });
    };
    seg('set-quality', v.quality);
    seg('set-invert', v.invertY);
    seg('set-fps', v.showFps);
    seg('set-fullscreen', v.fullscreen);
    seg('set-rival', v.rival);
    seg('set-ghost', v.ghost);
    seg('set-marker', v.marker);
    const handle = $('set-handle');
    if (handle && document.activeElement !== handle) handle.value = v.handle ?? '';
    const set = (id, value) => { const el = $(id); if (el) el.value = String(value); };
    set('set-sens', v.sensitivity);
    set('set-music', v.music);
    set('set-sfx', v.sfx);
    set('set-fov', v.fov);
  }

  // --------------------------------------------------------------- results

  /** The challenge landing: who ran what, and the button that is the
   *  gesture the pointer lock, fullscreen and audio all need. */
  showChallenge({ name, levelName, time }) {
    const line = $('ch-line');
    if (line) line.textContent = `${name} RAN ${levelName} IN ${Number(time).toFixed(2)}S`;
    this.replace('challenge');
  }

  /** Reflect the share round trip on the button itself: the results
   *  screen has no other place for it, and a toast dies unseen behind
   *  a native share sheet. */
  setShareState(state) {
    const b = $('rs-share');
    if (!b) return;
    b.disabled = state === 'busy';
    b.textContent = {
      idle: 'CHALLENGE A FRIEND', busy: 'MAKING THE LINK...',
      copied: 'LINK COPIED. SEND IT.', shared: 'LINK SENT',
      failed: 'NO SIGNAL. TRY AGAIN',
    }[state] ?? 'CHALLENGE A FRIEND';
  }

  showResults({
    level, time, best, isRecord, medal, topSpeed, maxDrop, hasNext,
    inRace = false, raceHost = false, shareable = false, verdict = null,
  }) {
    const medalEl = $('rs-medal');
    if (medalEl) {
      medalEl.className = `medal ${medal}`;
      medalEl.textContent = {
        gold: 'GOLD', silver: 'SILVER', bronze: 'BRONZE', none: 'COMPLETE',
      }[medal] ?? 'COMPLETE';
    }
    const t = (id, val) => { const el = $(id); if (el) el.textContent = val; };
    t('rs-name', isRecord ? 'NEW RECORD' : `${level.name} CLEAR`);
    t('rs-time', formatTime(time));
    t('rs-best', formatTime(best));
    t('rs-par', formatTime(level.mode?.par));
    t('rs-speed', `${topSpeed.toFixed(1)} M/S`);
    t('rs-drop', `${Math.round(maxDrop)} M`);
    const next = $('rs-next');
    // In a room, nobody wanders off alone: the host advances everyone.
    if (next) next.classList.toggle('hidden', !hasNext || inRace);
    $('rs-room')?.classList.toggle('hidden', !inRace);
    $('rs-share')?.classList.toggle('hidden', !shareable);
    this.setShareState('idle');
    const v = $('rs-verdict');
    if (v) { v.textContent = verdict ?? ''; v.classList.toggle('hidden', !verdict); }
    $('rs-rematch')?.classList.toggle('hidden', !(inRace && raceHost));
    $('rs-spectate')?.classList.toggle('hidden', !inRace);
    $('rs-pings')?.classList.toggle('hidden', !inRace);
    this.replace('results');
  }

  /**
   * Fill the results leaderboard. rows: [{rank, name, time, ghostKey, me}].
   * Arrives async after showResults; an empty call clears the section.
   */
  setBoard(rows, onRace, onWatch) {
    const el = $('rs-board');
    if (!el) return;
    el.textContent = '';
    if (!rows?.length) return;
    const head = document.createElement('div');
    head.className = 'bhead';
    head.textContent = 'WORLD TOP';
    el.appendChild(head);
    for (const r of rows) {
      const row = document.createElement('div');
      row.className = r.me ? 'brow me' : 'brow';
      const mk = (cls, text) => {
        const d = document.createElement('div');
        d.className = cls;
        d.textContent = text;
        row.appendChild(d);
      };
      mk('rk', String(r.rank));
      mk('nm', r.name);
      mk('tm', r.text ?? formatTime(r.time));
      // One grid cell holds the actions: RACE against anyone else's ghost,
      // WATCH anyone's (your own run is the one you study most).
      const actions = document.createElement('div');
      actions.style.display = 'flex';
      actions.style.gap = '6px';
      if (r.ghostKey && !r.me) {
        const b = document.createElement('button');
        b.textContent = 'RACE';
        b.addEventListener('click', () => { this.audio?.ui('click'); onRace?.(r); });
        actions.appendChild(b);
      }
      if (r.ghostKey && onWatch) {
        const w = document.createElement('button');
        w.textContent = 'WATCH';
        w.addEventListener('click', () => { this.audio?.ui('click'); onWatch(r); });
        actions.appendChild(w);
      }
      row.appendChild(actions);
      el.appendChild(row);
    }
  }

  _fillRaceLevels() {
    const sel = $('race-level');
    if (!sel || sel.options.length) return;
    for (const lv of LEVELS) {
      const o = document.createElement('option');
      o.value = lv.id;
      o.textContent = lv.name;
      sel.appendChild(o);
    }
  }

  /** One row per pad action: current binding + a rebind button. */
  _renderKeyBinds() {
    const host = $('key-binds');
    if (!host) return;
    host.textContent = '';
    const binds = loadKeyBinds(this.settings);
    for (const a of KEY_ACTIONS) {
      const row = document.createElement('div');
      row.className = 'setrow';
      const label = document.createElement('label');
      label.textContent = a.name;
      const ctl = document.createElement('div');
      ctl.className = 'ctl';
      const btn = document.createElement('button');
      btn.className = 'bindbtn';
      btn.textContent = keyLabel(binds[a.id]);
      btn.addEventListener('click', () => {
        this.audio?.ui('click');
        btn.textContent = 'PRESS A KEY...';
        this.on('key-rebind', {
          action: a.id,
          done: () => this._renderKeyBinds(),
        });
      });
      ctl.appendChild(btn);
      row.appendChild(label);
      row.appendChild(ctl);
      host.appendChild(row);
    }
  }

  _renderSync() {
    const el = $('sync-code');
    if (el) {
      const code = this.settings.get('syncCode');
      el.textContent = code ? formatSyncCode(code) : 'NONE YET';
    }
    this.setSyncStatus('');
  }

  /** Sync progress/result line; warn styles it as a failure. */
  setSyncStatus(text, warn = false) {
    const el = $('sync-status');
    if (!el) return;
    el.textContent = text ?? '';
    el.style.color = warn ? 'var(--danger)' : 'var(--cyan)';
    // A fresh code appears after the first upload.
    this._renderSyncCodeOnly?.();
  }

  _renderSyncCodeOnly() {
    const el = $('sync-code');
    const code = this.settings.get('syncCode');
    if (el && code) el.textContent = formatSyncCode(code);
  }

  _renderChangelog() {
    const host = $('changelog-list');
    if (!host) return;
    host.textContent = '';
    for (const rel of CHANGELOG) {
      const v = document.createElement('div');
      v.className = 'clv';
      v.textContent = `V${rel.v}  //  ${rel.date}`;
      host.appendChild(v);
      for (const item of rel.items) {
        const li = document.createElement('div');
        li.className = 'cli';
        li.textContent = `- ${item}`;
        host.appendChild(li);
      }
    }
  }

  /** First-run naming gate; runs cb after the pilot commits either way. */
  promptCallsign(cb) {
    const input = $('first-handle');
    const suggestion = 'PILOT-' + Math.floor(100 + Math.random() * 900);
    if (input) { input.value = ''; input.placeholder = suggestion; }
    this._callsignGo = cb;
    this._callsignDefault = suggestion;
    this.show('callsign', { replace: true });
    input?.focus();
  }

  _renderPadBinds() {
    const host = $('pad-binds');
    if (!host) return;
    host.textContent = '';
    const binds = loadBinds(this.settings);
    for (const a of PAD_ACTIONS) {
      const row = document.createElement('div');
      row.className = 'setrow';
      const label = document.createElement('label');
      label.textContent = a.name;
      const ctl = document.createElement('div');
      ctl.className = 'ctl';
      const btn = document.createElement('button');
      btn.className = 'bindbtn';
      btn.textContent = bindLabel(binds, a.id);
      btn.addEventListener('click', () => {
        this.audio?.ui('click');
        btn.textContent = 'PRESS A BUTTON...';
        this.on('pad-rebind', {
          action: a.id,
          done: () => this._renderPadBinds(),
        });
      });
      ctl.appendChild(btn);
      row.appendChild(label);
      row.appendChild(ctl);
      host.appendChild(row);
    }
  }

  /**
   * Pad-driven menu focus: d-pad or stick moves it, A activates, B backs
   * out. Focus lives on the visible screen's buttons.
   */
  padMenu(op) {
    const active = document.querySelector('.screen.on');
    if (!active) return;
    const items = [...active.querySelectorAll('button')]
      .filter((b) => !b.classList.contains('hidden') && b.getClientRects().length > 0);
    if (!items.length) return;
    let idx = items.findIndex((b) => b.classList.contains('pad-focus'));
    if (op === 'up' || op === 'down') {
      if (idx >= 0) items[idx].classList.remove('pad-focus');
      idx = idx < 0 ? (op === 'down' ? 0 : items.length - 1)
        : (idx + (op === 'down' ? 1 : items.length - 1)) % items.length;
      items[idx].classList.add('pad-focus');
    } else if (op === 'select') {
      (idx >= 0 ? items[idx] : items[0]).click();
    } else if (op === 'back') {
      const back = active.querySelector('[data-back]');
      back?.click();
    }
  }

  setPauseRoomVisible(v) {
    $('pz-room')?.classList.toggle('hidden', !v);
  }

  /** Race lobby state; null clears it. */
  setRaceStatus(st) {
    const status = $('race-status');
    const roster = $('race-roster');
    const start = $('race-start');
    const code = $('race-code');
    if (!st) {
      if (status) status.textContent = "Create a room, or join a friend's code.";
      if (roster) roster.textContent = '';
      start?.classList.add('hidden');
      $('race-pings')?.classList.add('hidden');
      this._voted = false;
      return;
    }
    $('race-pings')?.classList.toggle('hidden', !st.connected);
    if (!st.connected) this._voted = false;
    if (code && st.code) code.value = st.code;
    if (status) {
      status.textContent = st.connected
        ? `ROOM ${st.code}  //  ${st.roster.length}/8 PILOTS  //  majority vote picks the sector`
        : `JOINING ${st.code}...`;
    }
    // Everyone votes; the picker is the ballot. Cast the default once on
    // connect so every pilot counts even if they never touch it.
    $('race-pickrow')?.classList.remove('hidden');
    if (st.connected && !this._voted) {
      this._voted = true;
      this.on('race-vote', $('race-level')?.value ?? 'boot');
    }
    if (roster) {
      roster.textContent = '';
      for (const p of st.roster) {
        // Real grid cells: a lone text node lands in the 2.2ch rank column
        // and the whole roster crams against the left edge.
        const row = document.createElement('div');
        row.className = 'brow';
        const rk = document.createElement('span');
        rk.className = 'rk';
        rk.textContent = '>';
        const nm = document.createElement('span');
        nm.className = 'nm';
        nm.textContent = p.name;
        const vote = document.createElement('span');
        vote.textContent = p.vote ? String(p.vote).toUpperCase() : '';
        const tm = document.createElement('span');
        tm.className = 'tm';
        tm.textContent = p.finished != null ? `${p.finished.toFixed(2)}s` : '';
        row.append(rk, nm, vote, tm);
        roster.appendChild(row);
      }
    }
    start?.classList.toggle('hidden', !st.isHost);
  }

  /**
   * A quick-ping lands wherever you are: the feed floats above every
   * screen, because the lobby is where most pings arrive and the HUD
   * toast layer sits underneath the menus.
   */
  showPing(text) {
    const feed = $('pingfeed');
    if (!feed) return;
    const el = document.createElement('div');
    el.className = 'ping';
    el.textContent = text;
    feed.appendChild(el);
    while (feed.children.length > 4) feed.firstChild.remove();
    requestAnimationFrame(() => el.classList.add('show'));
    setTimeout(() => {
      el.classList.remove('show');
      setTimeout(() => el.remove(), 300);
    }, 2800);
  }

  /** The room's recent races, newest first; empty hides the panel. */
  setRaceHistory(races) {
    const el = $('race-history');
    if (!el) return;
    el.textContent = '';
    const shown = (races ?? []).filter((r) => r.results?.length).slice(0, 5);
    el.classList.toggle('hidden', !shown.length);
    for (const r of shown) {
      const row = document.createElement('div');
      row.className = 'brow wide';
      const line = r.results.map((x, i) => `${i + 1}. ${x.name} ${x.time.toFixed(2)}s`).join('   ');
      row.textContent = `${String(r.level).toUpperCase()}  //  ${line}`;
      el.appendChild(row);
    }
  }

  showDead(reason, { canRespawn = true } = {}) {
    const el = $('dead-reason');
    if (el) el.textContent = reason;
    // A mode-level fail (timer out) ended the RUN, not the life: offering
    // a checkpoint reboot there resumes into a finished mode.
    $('dz-respawn')?.classList.toggle('hidden', !canRespawn);
    this.replace('dead');
  }
}
